import { useTranslation, Trans } from 'react-i18next';
import type { Report } from '@reports/domain/entities';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@parrot-software/design-system';
import { Play, HelpCircle } from '@parrot-software/design-system/icons';

interface ReportHeaderProps {
  report: Report;
}

/**
 * ReportHeader - Header component for reports
 *
 * Displays report title with play icon (link to documentation),
 * description with help icon (tooltip).
 *
 * @param {ReportHeaderProps} props - Component properties
 * @param {Report} props.report - Report entity with metadata
 * @param {string} [props.documentationUrl] - URL to documentation/tutorial
 * @returns {React.ReactElement} Report header component
 *
 * @example
 * <ReportHeader
 *   report={salesReport}
 *   documentationUrl="https://docs.parrot.rest/reports/sales"
 * />
 */
export const ReportHeader = ({ report }: ReportHeaderProps) => {
  const { t } = useTranslation();

  const handlePlayClick = () => {
    if (report.metadata.parrotAcademyLink) {
      window.open(report.metadata.parrotAcademyLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="mb-6">
      {/* Title with Play Icon */}
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl font-semibold text-(--base-foreground)">{t(report.metadata.name)}</h1>
        {report.metadata.parrotAcademyLink && (
          <Button
            aria-label="Ver tutorial"
            className="w-6 h-6 rounded-full border-[1.5px] border-(--base-primary-primary) text-(--base-primary-primary) bg-transparent hover:bg-red-50 hover:text-(--base-primary-primary) transition-colors"
            onClick={handlePlayClick}
            size="icon"
            title="Ver tutorial"
            variant="ghost"
          >
            <Play className="w-[12px]! h-[12px]!" />
          </Button>
        )}
      </div>

      {/* Description with Help Icon */}
      {report.metadata.description && (
        <div className="flex items-start gap-2">
          <p className="text-base text-muted-foreground">{t(report.metadata.description)}</p>
          {report.metadata.tooltip && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle
                    className="text-(--feedback-info) cursor-pointer shrink-0 w-6 h-6"
                    size={24}
                    strokeWidth={1.3}
                  />
                </TooltipTrigger>
                <TooltipContent
                  align="center"
                  className="font-sans max-w-sm bg-background text-foreground border shadow-lg px-4 py-3 text-sm leading-6"
                  side="bottom"
                >
                  <div className="font-normal">
                    <Trans
                      components={{
                        strong: <strong className="font-semibold" />,
                        br: <br />,
                        ul: <ul className="pl-5 mt-2 list-disc" />,
                        li: <li className="leading-relaxed" />,
                      }}
                    >
                      {t(report.metadata.tooltip)}
                    </Trans>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  );
};
